import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiFilter, FiX, FiSearch, FiChevronRight } from 'react-icons/fi';
import API from '../utils/api';
import ProductCard from '../components/common/ProductCard';
import './ProductsPage.css';

const SORT_OPTIONS = [
  { value: 'newest',  label: 'Newest'         },
  { value: 'name',    label: 'Name A–Z'        },
  { value: 'price',   label: 'Price: Low–High' },
];

/* ── Skeleton card ── */
const SkeletonCard = () => (
  <div className="product-card skeleton-card">
    <div className="skeleton-img" />
    <div className="product-card-body">
      <div className="skeleton-line short" />
      <div className="skeleton-line" />
      <div className="skeleton-line medium" />
    </div>
  </div>
);

/* ── Session ID helper (mirrors Navbar) ── */
const getSessionId = () => {
  let sid = sessionStorage.getItem('ap_sid');
  if (!sid) {
    sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('ap_sid', sid);
  }
  return sid;
};

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [categories,    setCategories]    = useState([]);
  const [products,      setProducts]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [catLoading,    setCatLoading]    = useState(true);
  const [totalCount,    setTotalCount]    = useState(0);
  const [totalPages,    setTotalPages]    = useState(1);
  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const [localSearch,   setLocalSearch]   = useState(searchParams.get('search') || '');

  const categoryId = searchParams.get('categoryId') || '';
  const search     = searchParams.get('search')     || '';
  const sort       = searchParams.get('sort')       || 'newest';
  const page       = parseInt(searchParams.get('page') || '1');

  // Track last logged search to avoid duplicate logs on re-renders
  const lastLoggedRef = useRef('');

  // Load categories for sidebar
  useEffect(() => {
    setCatLoading(true);
    API.get('/catalog/public/categories?status=active')
      .then(r => setCategories(r.data.categories || []))
      .catch(() => {})
      .finally(() => setCatLoading(false));
  }, []);

  // Load products
  const loadProducts = useCallback(() => {
    setLoading(true);
    // status=active is now also enforced server-side for public routes,
    // but we still send it for defence-in-depth and clarity.
    const params = new URLSearchParams({ page, limit: 12, sort, status: 'active' });
    if (categoryId) params.set('categoryId', categoryId);
    if (search)     params.set('search', search);

    API.get(`/catalog/public/products?${params}`)
      .then(r => {
        const prods = r.data.products || [];
        const count = r.data.count    || 0;
        setProducts(prods);
        setTotalCount(count);
        setTotalPages(r.data.pages || 1);

        // ── Log search analytics once per unique query ─────────────
        if (search && search !== lastLoggedRef.current) {
          lastLoggedRef.current = search;
          API.post('/catalog/public/search/log', {
            query:       search,
            resultCount: count,
            source:      'sidebar',
            sessionId:   getSessionId(),
          }).catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, sort, categoryId, search]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  // Sync local search input when URL param changes from outside (e.g. navbar search)
  useEffect(() => {
    setLocalSearch(searchParams.get('search') || '');
  }, [searchParams]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    next.set('page', '1');
    setSearchParams(next);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    updateParam('search', localSearch.trim());
  };

  const activeCat = categories.find(c => String(c.id) === String(categoryId));
  const pageTitle = activeCat ? activeCat.name : 'All Products';

  return (
    <>
      <Helmet>
        <title>{activeCat?.seoTitle || `${pageTitle} | Angel Printers`}</title>
        <meta name="description" content={activeCat?.seoDescription || 'Browse our complete range of premium print products.'} />
      </Helmet>

      <div className="products-page">
        {/* Header */}
        <div className="products-header">
          <div className="container">
            <nav className="breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <FiChevronRight size={13} />
              <span>Products</span>
              {activeCat && (
                <>
                  <FiChevronRight size={13} />
                  <span>{activeCat.name}</span>
                </>
              )}
            </nav>
            <h1>{pageTitle}</h1>
          </div>
        </div>

        <div className="container products-layout">
          {/* ── Sidebar ── */}
          {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}
          <aside className={`products-sidebar ${sidebarOpen ? 'open' : ''}`} aria-label="Filters">
            <div className="sidebar-header">
              <h3>Filters</h3>
              <button className="icon-btn" onClick={() => setSidebarOpen(false)} aria-label="Close filters">
                <FiX size={20} />
              </button>
            </div>

            {/* Search */}
            <div className="sidebar-section">
              <h4>Search</h4>
              <form onSubmit={handleSearch} className="sidebar-search">
                <input
                  className="form-control"
                  placeholder="Search products…"
                  value={localSearch}
                  onChange={e => setLocalSearch(e.target.value)}
                  aria-label="Search products"
                  autoComplete="off"
                />
                <button type="submit" className="btn btn-primary btn-sm" style={{ marginTop: 8 }}>
                  <FiSearch size={14} /> Search
                </button>
              </form>
            </div>

            {/* Categories */}
            <div className="sidebar-section">
              <h4>Category</h4>
              <div className="cat-list">
                <button
                  className={`cat-btn ${!categoryId ? 'active' : ''}`}
                  onClick={() => updateParam('categoryId', '')}
                >
                  All Products
                </button>
                {catLoading && <div className="skeleton-line" style={{ margin: '6px 12px' }} />}
                {categories.map(c => (
                  <button
                    key={c.id}
                    className={`cat-btn ${String(categoryId) === String(c.id) ? 'active' : ''}`}
                    onClick={() => { updateParam('categoryId', c.id); setSidebarOpen(false); }}
                  >
                    {c.name}
                    {c.subcategories?.length > 0 && (
                      <span className="cat-sub-count">{c.subcategories.length}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Sort */}
            <div className="sidebar-section">
              <h4>Sort By</h4>
              <select className="form-control" value={sort} onChange={e => updateParam('sort', e.target.value)}>
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            {/* Clear */}
            {(categoryId || search) && (
              <button
                className="btn btn-outline btn-sm"
                style={{ width: '100%', marginTop: 4 }}
                onClick={() => { setSearchParams({}); setLocalSearch(''); }}
              >
                Clear All Filters
              </button>
            )}
          </aside>

          {/* ── Main ── */}
          <main className="products-main">
            {/* Toolbar */}
            <div className="products-toolbar">
              <button
                className="btn btn-outline btn-sm filter-toggle"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open filters"
              >
                <FiFilter size={15} /> Filters
              </button>
              <span className="products-count">{totalCount} product{totalCount !== 1 ? 's' : ''}</span>
              <div style={{ flex: 1 }} />
              <select
                className="form-control toolbar-sort"
                value={sort}
                onChange={e => updateParam('sort', e.target.value)}
                aria-label="Sort"
              >
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            {/* Active filter tags */}
            {(categoryId || search) && (
              <div className="active-filters">
                {activeCat && (
                  <span className="filter-tag">
                    {activeCat.name}
                    <button onClick={() => updateParam('categoryId', '')} aria-label="Remove category filter"><FiX size={12} /></button>
                  </span>
                )}
                {search && (
                  <span className="filter-tag">
                    "{search}"
                    <button onClick={() => { updateParam('search', ''); setLocalSearch(''); }} aria-label="Remove search filter"><FiX size={12} /></button>
                  </span>
                )}
              </div>
            )}

            {/* Category tiles (when no filter active) */}
            {!categoryId && !search && categories.length > 0 && (
              <div className="category-tiles">
                {categories.map(c => (
                  <Link
                    key={c.id}
                    to={`/${c.slug}`}
                    className="category-tile"
                  >
                    {c.image && <img src={c.image} alt={c.name} loading="lazy" />}
                    <div className="category-tile-body">
                      <span className="category-tile-name">{c.name}</span>
                      {c.description && <span className="category-tile-desc">{c.description}</span>}
                    </div>
                    <FiChevronRight size={16} className="category-tile-arrow" />
                  </Link>
                ))}
              </div>
            )}

            {/* Product grid */}
            {loading ? (
              <div className="products-grid">
                {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
              </div>
            ) : products.length === 0 ? (
              <div className="no-products">
                <div className="no-products-icon">📦</div>
                <h3>No products found</h3>
                {search ? (
                  <>
                    <p>We couldn't find anything for "<strong>{search}</strong>".</p>
                    <p className="no-products-hint">Try checking the spelling, using fewer words, or browsing by category below.</p>
                    <div className="no-products-actions">
                      <button
                        className="btn btn-primary"
                        onClick={() => { setSearchParams({}); setLocalSearch(''); }}
                      >
                        Browse All Products
                      </button>
                      {categories.length > 0 && (
                        <div className="no-products-cats">
                          {categories.slice(0, 4).map(c => (
                            <Link key={c.id} to={`/${c.slug}`} className="no-products-cat-link">
                              {c.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <p>Check back soon.</p>
                    <button
                      className="btn btn-primary"
                      onClick={() => { setSearchParams({}); setLocalSearch(''); }}
                    >
                      View All Products
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="products-grid">
                {products.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button className="page-btn" disabled={page <= 1} onClick={() => updateParam('page', page - 1)}>← Prev</button>
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page + i - 3;
                  if (p < 1 || p > totalPages) return null;
                  return (
                    <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => updateParam('page', p)}>{p}</button>
                  );
                })}
                <button className="page-btn" disabled={page >= totalPages} onClick={() => updateParam('page', page + 1)}>Next →</button>
              </div>
            )}
          </main>
        </div>
      </div>
    </>
  );
};

export default ProductsPage;